import { createCalculatorProvenanceSegmentResolver } from "./calculator-provenance-projection.js";

function projectionScopeKey(projection) {
    const revision = projection?.modelRevision;
    if (typeof revision !== "string" || revision.length === 0) {
        return null;
    }
    const hash = projection.userLayerContentHash;
    if (hash === null) {
        return `${revision}\u0000none`;
    }
    if (
        !hash
        || typeof hash !== "object"
        || typeof hash.algorithm !== "string"
        || typeof hash.digest !== "string"
    ) {
        return null;
    }
    return `${revision}\u0000${hash.algorithm}:${hash.digest}`;
}

/**
 * Owns validated, revision-bound semantic provenance results across numeric
 * scenario projections. Operation and layer identities are part of every
 * entry key, so rebuilding the immutable operation index also invalidates
 * display names and exact source metadata even if a transport commitment were
 * accidentally reused.
 */
export function createLootSankeyProjectorCache() {
    let activeScopeKey = null;
    let entries = new Map();
    let objectIds = new WeakMap();
    let nextObjectId = 1;

    return Object.freeze({
        forProjection(projection) {
            const scopeKey = projectionScopeKey(projection);
            if (scopeKey === null || scopeKey !== activeScopeKey) {
                activeScopeKey = scopeKey;
                entries = new Map();
                objectIds = new WeakMap();
                nextObjectId = 1;
            }
            if (scopeKey === null) {
                return null;
            }
            return Object.freeze({
                has(key) {
                    return entries.has(key);
                },
                get(key) {
                    return entries.get(key);
                },
                set(key, value) {
                    entries.set(key, value);
                    return value;
                },
                objectId(value) {
                    if (!value || typeof value !== "object") {
                        return `primitive:${String(value)}`;
                    }
                    if (!objectIds.has(value)) {
                        objectIds.set(value, nextObjectId);
                        nextObjectId += 1;
                    }
                    return objectIds.get(value);
                },
            });
        },
    });
}

/**
 * Creates the invariant services for one typed projection. The caller retains
 * ownership of numeric row projection while this context owns static
 * presentation and semantic provenance reuse.
 */
export function createLootSankeyProjectionContext({
    formatters,
    metricPresentations,
    order,
    parseGroupPresentation,
    parseItemPresentation,
    presentation,
    priceResolver,
    projectionRecord,
    projectorCache = null,
    sankey,
}) {
    const provenanceResolver = createCalculatorProvenanceSegmentResolver(
        projectionRecord,
        presentation,
        {
            cache: projectorCache?.forProjection(projectionRecord) ?? null,
        },
    );
    const groupPresentationById = new Map();
    const itemPresentationById = new Map();
    const groupProvenanceById = new Map();
    const itemProvenanceByKey = new Map();

    return Object.freeze({
        formatters,
        metricPresentations,
        order,
        presentation,
        priceResolver,
        projectionRecord,
        sankey,
        groupPresentation(groupId) {
            if (!groupPresentationById.has(groupId)) {
                groupPresentationById.set(
                    groupId,
                    Object.freeze(parseGroupPresentation(groupId)),
                );
            }
            return groupPresentationById.get(groupId);
        },
        itemPresentation(itemId) {
            if (!itemPresentationById.has(itemId)) {
                itemPresentationById.set(
                    itemId,
                    Object.freeze(parseItemPresentation(itemId)),
                );
            }
            return itemPresentationById.get(itemId);
        },
        groupProvenance(traceIds, groupId, role, path) {
            const key = `${groupId}\u0000${role}`;
            if (!groupProvenanceById.has(key)) {
                groupProvenanceById.set(
                    key,
                    provenanceResolver.resolveSemanticGroup(
                        traceIds,
                        groupId,
                        role,
                        path,
                    ),
                );
            }
            return groupProvenanceById.get(key);
        },
        itemProvenance(
            traceIds,
            itemId,
            groupId,
            knownZeroRate,
            path,
            representMissingItemRate = false,
        ) {
            const key = [
                groupId,
                itemId,
                knownZeroRate === true ? "zero" : "nonzero",
                representMissingItemRate === true ? "missing-rate" : "rated",
            ].join("\u0000");
            if (!itemProvenanceByKey.has(key)) {
                itemProvenanceByKey.set(
                    key,
                    representMissingItemRate
                        ? provenanceResolver.resolveInspectorItem(
                            traceIds,
                            itemId,
                            groupId,
                            path,
                        )
                        : provenanceResolver.resolveSemanticItem(
                            traceIds,
                            itemId,
                            groupId,
                            path,
                            { knownZeroRate },
                        ),
                );
            }
            return itemProvenanceByKey.get(key);
        },
    });
}
